import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Scissors, Play, Pause, SkipBack, SkipForward, Check, X, RotateCcw, Loader2 } from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';

/**
 * VideoTrimmer — Human-in-the-loop video curation tool.
 *
 * Renders inline below the video player controls with a visual timeline
 * showing the full video, dual IN/OUT handles, and a preview of the selected range.
 *
 * Props:
 *   videoRef      - ref to the <video> element
 *   videoUrl      - URL of the video (blob: or https:)
 *   duration      - video duration in seconds
 *   onTrimConfirm - (startMs, endMs) => void — called when user confirms trim
 *   onCancel      - () => void
 *   isProcessing  - bool — show loading state while backend processes
 */
const VideoTrimmer = ({ videoRef, videoUrl, duration, onTrimConfirm, onCancel, isProcessing = false }) => {
  const [trimStart, setTrimStart] = useState(0);       // seconds
  const [trimEnd, setTrimEnd] = useState(duration);     // seconds
  const [dragging, setDragging] = useState(null);       // 'start' | 'end' | null
  const [previewing, setPreviewing] = useState(false);
  const timelineRef = useRef(null);
  const animRef = useRef(null);
  const [playheadPos, setPlayheadPos] = useState(0);    // 0-1

  useEffect(() => {
    if (duration > 0 && trimEnd === 0) setTrimEnd(duration);
  }, [duration]);

  // Keep playhead synced
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;
    const update = () => {
      if (video.duration > 0) {
        setPlayheadPos(video.currentTime / video.duration);
      }
      animRef.current = requestAnimationFrame(update);
    };
    animRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animRef.current);
  }, [videoRef]);

  const formatTime = (s) => {
    if (!s || !isFinite(s)) return '0:00.00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    const ms = Math.floor((s % 1) * 100);
    return `${m}:${String(sec).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
  };

  const trimDuration = trimEnd - trimStart;
  const startPct = duration > 0 ? (trimStart / duration) * 100 : 0;
  const endPct = duration > 0 ? (trimEnd / duration) * 100 : 100;
  const playheadPct = playheadPos * 100;

  // Mouse handlers for dragging handles
  const handleMouseDown = useCallback((handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(handle);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !timelineRef.current || !duration) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = pct * duration;

    if (dragging === 'start') {
      const newStart = Math.max(0, Math.min(time, trimEnd - 3)); // min 3s clip
      setTrimStart(newStart);
      if (videoRef?.current) videoRef.current.currentTime = newStart;
    } else if (dragging === 'end') {
      const newEnd = Math.min(duration, Math.max(time, trimStart + 3));
      setTrimEnd(newEnd);
      if (videoRef?.current) videoRef.current.currentTime = newEnd;
    }
  }, [dragging, duration, trimStart, trimEnd, videoRef]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  // Touch handlers for mobile
  const handleTouchMove = useCallback((e) => {
    if (!dragging || !timelineRef.current || !duration) return;
    const touch = e.touches[0];
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    const time = pct * duration;

    if (dragging === 'start') {
      setTrimStart(Math.max(0, Math.min(time, trimEnd - 3)));
      if (videoRef?.current) videoRef.current.currentTime = Math.max(0, Math.min(time, trimEnd - 3));
    } else {
      setTrimEnd(Math.min(duration, Math.max(time, trimStart + 3)));
      if (videoRef?.current) videoRef.current.currentTime = Math.min(duration, Math.max(time, trimStart + 3));
    }
  }, [dragging, duration, trimStart, trimEnd, videoRef]);

  const handleTouchEnd = useCallback(() => setDragging(null), []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [dragging, handleTouchMove, handleTouchEnd]);

  // Set IN / OUT to current video position
  const setInPoint = () => {
    const t = videoRef?.current?.currentTime || 0;
    if (t < trimEnd - 3) setTrimStart(t);
  };
  const setOutPoint = () => {
    const t = videoRef?.current?.currentTime || 0;
    if (t > trimStart + 3) setTrimEnd(t);
  };

  // Preview: play only the trimmed section
  const previewClip = () => {
    const video = videoRef?.current;
    if (!video) return;
    video.currentTime = trimStart;
    video.play();
    setPreviewing(true);

    const checkEnd = () => {
      if (video.currentTime >= trimEnd) {
        video.pause();
        video.currentTime = trimStart;
        setPreviewing(false);
      } else if (previewing) {
        requestAnimationFrame(checkEnd);
      }
    };
    requestAnimationFrame(checkEnd);
  };

  // Reset to full video
  const resetTrim = () => {
    setTrimStart(0);
    setTrimEnd(duration);
    if (videoRef?.current) videoRef.current.currentTime = 0;
  };

  const handleConfirm = () => {
    const startMs = Math.round(trimStart * 1000);
    const endMs = Math.round(trimEnd * 1000);
    onTrimConfirm(startMs, endMs);
  };

  // Click on timeline to seek
  const seekOnTimeline = (e) => {
    if (dragging) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const time = pct * duration;
    if (videoRef?.current) videoRef.current.currentTime = time;
  };

  return (
    <div
      className="rounded-xl border-2 p-4 space-y-3 mt-2 select-none"
      style={{ borderColor: '#f59e0b', backgroundColor: '#fffbeb' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scissors size={16} style={{ color: '#d97706' }} />
          <span className="text-sm font-black" style={{ color: VUKZ.text }}>
            Cortar Vídeo
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
            {formatTime(trimDuration)}
          </span>
        </div>
        <button onClick={onCancel} className="p-1 rounded hover:bg-amber-100 transition-all">
          <X size={16} style={{ color: VUKZ.textLight }} />
        </button>
      </div>

      {/* Visual Timeline */}
      <div className="space-y-1">
        <div
          ref={timelineRef}
          className="relative h-12 rounded-lg cursor-pointer overflow-hidden"
          style={{ backgroundColor: '#e5e7eb' }}
          onClick={seekOnTimeline}
        >
          {/* Dimmed outside regions */}
          <div
            className="absolute inset-y-0 left-0 rounded-l-lg"
            style={{ width: `${startPct}%`, backgroundColor: 'rgba(0,0,0,0.4)' }}
          />
          <div
            className="absolute inset-y-0 right-0 rounded-r-lg"
            style={{ width: `${100 - endPct}%`, backgroundColor: 'rgba(0,0,0,0.4)' }}
          />

          {/* Selected region (highlighted) */}
          <div
            className="absolute inset-y-0"
            style={{
              left: `${startPct}%`,
              width: `${endPct - startPct}%`,
              backgroundColor: 'rgba(245, 158, 11, 0.25)',
              borderTop: '3px solid #f59e0b',
              borderBottom: '3px solid #f59e0b',
            }}
          />

          {/* IN handle */}
          <div
            className="absolute inset-y-0 w-4 cursor-ew-resize z-10 flex items-center justify-center group"
            style={{ left: `calc(${startPct}% - 8px)` }}
            onMouseDown={(e) => handleMouseDown('start', e)}
            onTouchStart={(e) => { e.preventDefault(); setDragging('start'); }}
          >
            <div
              className="w-1.5 h-8 rounded-full transition-all group-hover:h-10 group-hover:w-2"
              style={{ backgroundColor: '#d97706' }}
            />
          </div>

          {/* OUT handle */}
          <div
            className="absolute inset-y-0 w-4 cursor-ew-resize z-10 flex items-center justify-center group"
            style={{ left: `calc(${endPct}% - 8px)` }}
            onMouseDown={(e) => handleMouseDown('end', e)}
            onTouchStart={(e) => { e.preventDefault(); setDragging('end'); }}
          >
            <div
              className="w-1.5 h-8 rounded-full transition-all group-hover:h-10 group-hover:w-2"
              style={{ backgroundColor: '#d97706' }}
            />
          </div>

          {/* Current playhead */}
          <div
            className="absolute inset-y-0 w-0.5 z-20 pointer-events-none"
            style={{ left: `${playheadPct}%`, backgroundColor: 'white', boxShadow: '0 0 4px rgba(0,0,0,0.5)' }}
          />

          {/* Time labels on timeline */}
          <div className="absolute bottom-0.5 left-1 text-[8px] font-mono font-bold" style={{ color: 'rgba(255,255,255,0.8)' }}>
            0:00
          </div>
          <div className="absolute bottom-0.5 right-1 text-[8px] font-mono font-bold" style={{ color: 'rgba(255,255,255,0.8)' }}>
            {formatTime(duration)}
          </div>
        </div>

        {/* IN/OUT time display */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>IN</span>
            <span className="text-[11px] font-mono font-bold" style={{ color: VUKZ.text }}>{formatTime(trimStart)}</span>
          </div>
          <div className="text-[10px] font-bold" style={{ color: '#d97706' }}>
            ← {formatTime(trimDuration)} →
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-mono font-bold" style={{ color: VUKZ.text }}>{formatTime(trimEnd)}</span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>OUT</span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-2">
          {/* Set IN at current position */}
          <button
            onClick={setInPoint}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all hover:bg-amber-50"
            style={{ borderColor: '#fbbf24', color: '#92400e' }}
            title="Marcar IN na posição atual do vídeo"
          >
            <SkipBack size={12} /> Definir IN
          </button>
          {/* Set OUT at current position */}
          <button
            onClick={setOutPoint}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all hover:bg-amber-50"
            style={{ borderColor: '#fbbf24', color: '#92400e' }}
            title="Marcar OUT na posição atual do vídeo"
          >
            Definir OUT <SkipForward size={12} />
          </button>
          {/* Preview clip */}
          <button
            onClick={previewClip}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:bg-gray-100"
            style={{ color: VUKZ.textLight }}
          >
            <Play size={12} /> Preview
          </button>
          {/* Reset */}
          <button
            onClick={resetTrim}
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:bg-gray-100"
            style={{ color: VUKZ.textLight }}
            title="Resetar para vídeo completo"
          >
            <RotateCcw size={12} />
          </button>
        </div>

        {/* Confirm trim */}
        <button
          onClick={handleConfirm}
          disabled={isProcessing || trimDuration < 3}
          className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
          style={{ background: VUKZ_GRADIENT, color: 'white' }}
        >
          {isProcessing ? (
            <><Loader2 size={14} className="animate-spin" /> Cortando...</>
          ) : (
            <><Scissors size={14} /> Cortar e Analisar</>
          )}
        </button>
      </div>
    </div>
  );
};

export default VideoTrimmer;
